import { CollectionRuleType } from 'domains/collections/components/collections-form/components/rules/utils/CollectionsRules.types'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { type GetCollectionQuery } from 'domains/collections/api'

export const collectionsFormTestIds = {
  form: 'collections-form',
  nameField: 'collections-form-name-field',
  nameFieldErrorMessage: 'collections-form-name-field-error-message',
  formError: 'collections-form-general-error',
  submitButton: 'collections-form-submit-button',
  tileField: 'collections-form-tile-field',
  tileFieldErrorMessage: 'collections-form-tile-field-error-message',
  mobileHeaderField: 'collections-form-mobile-header-field',
  webHeaderField: 'collections-form-web-header-field',
  headerTitleField: 'collections-form-header-title-field',
  headerDisplayStyleField: 'collections-form-header-display-style-field',
  headerDescriptionField: 'collections-form-header-description-field',
  headerColorField: 'collections-form-header-color-field',
  headerBackgroundColorField: 'collections-form-header-background-color-field',
  storefrontBannerHeadingField: 'collections-form-storefront-banner-heading-field',
  storefrontBannerHeadingFieldErrorMessage:
    'collections-form-storefront-banner-heading-field-error-message',
  storefrontBannerActiveField: 'collections-form-storefront-banner-active-field',
  storefrontBannerHeadingColorField: 'collections-form-storefront-banner-heading-color-field',
  storefrontBannerHeadingColorFieldErrorMessage:
    'collections-form-storefront-banner-heading-color-field-error-message',
  storefrontBannerSubHeadingField: 'collections-form-storefront-banner-sub-heading-field',
  storefrontBannerSubHeadingFieldErrorMessage:
    'collections-form-storefront-banner-sub-heading-field-error-message',
  storefrontBannerSubHeadingColorField:
    'collections-form-storefront-banner-sub-heading-color-field',
  storefrontBannerSubHeadingColorFieldErrorMessage:
    'collections-form-storefront-banner-sub-heading-color-field-error-message',
  storefrontBannerTagField: 'collections-form-storefront-banner-tag-field',
  storefrontBannerTagColorField: 'collections-form-storefront-banner-tag-color-field',
  storefrontBannerCTAField: 'collections-form-storefront-banner-cta-field',
  storefrontBannerCTAFieldErrorMessage:
    'collections-form-storefront-banner-cta-field-error-message',
  storefrontBannerBackgroundColorField: 'collections-form-storefront-banner-background-color-field',
  storefrontBannerImageField: 'collections-form-storefront-banner-image-field',
  storefrontBannerImageFieldErrorMessage:
    'collections-form-storefront-banner-image-field-error-message',
  startDateField: 'collections-form-start-date-field',
  endDateField: 'collections-form-end-date-field',
  noEndDateField: 'collections-form-no-end-date-field',
  tileToggle: 'collections-form-tile-toggle',
  headerToggle: 'collections-form-header-toggle',
  storefrontBannerToggle: 'collections-form-storefront-banner-toggle',
}

export const collectionsFormEmptyData: CollectionsFormData = {
  // Used for toggling fragments of the form, they won't end up on the resulting data
  showTileFragment: false,
  showHeaderFragment: false,
  showStorefrontBannerFragment: false,
  details: {
    name: '',
    startDate: '',
    endDate: '',
    parentId: '',
    retailerIds: [],
    active: false,
    activeAdminOnly: false,
  },
  tile: {
    image: '',
  },
  header: {
    title: '',
    description: '',
    backgroundColor: '',
    color: '',
    mobileImage: '',
    webImage: '',
  },
  storefrontBanner: {
    active: true,
    heading: '',
    headingColor: '',
    subHeading: '',
    subHeadingColor: '',
    tag: '',
    tagColor: '',
    cta: '',
    backgroundColor: '',
    bannerImage: '',
  },
  rules: [],
  childrenIds: [],
  validation: {
    treeStructure: null,
  },
}

const mapApiSubjectsToRules = (
  subjects: GetCollectionQuery['collection']['subjects']
): CollectionsFormData['rules'] => {
  if (!subjects) return []

  return subjects.map(subject => {
    // if criteriaProductIds has values then it is a code rule
    if (subject.criteriaProductIds?.length > 0) {
      return {
        type: CollectionRuleType.productCodes,
        name: subject.subjectName,
        productIds: subject.criteriaProductIds,
        subjectId: subject.id,
      }
    }

    // if criteriaTerms has values then it is a searchTerm rule
    if (subject.criteriaTerms?.length > 0) {
      return {
        type: CollectionRuleType.searchTerm,
        name: subject.subjectName,
        searchTerm: subject.criteriaTerms[0],
        subjectId: subject.id,
      }
    }
  })
}

export const mapCollectionDataToFormData = (
  collection: GetCollectionQuery['collection']
): CollectionsFormData => {
  const {
    name,
    startDate,
    endDate,
    parentId,
    requiredRetailerIds,
    imageUrl,
    header,
    storefrontBanner,
    subjects,
    children,
    active,
    activeAdminOnly,
  } = collection

  return {
    ...collectionsFormEmptyData,
    // Existence of following implies the block should be shown or not
    showHeaderFragment: Boolean(header),
    showTileFragment: Boolean(imageUrl),
    showStorefrontBannerFragment: Boolean(storefrontBanner),
    details: {
      name: name,
      startDate: startDate,
      endDate: endDate,
      parentId: parentId,
      retailerIds: requiredRetailerIds,
      active,
      activeAdminOnly,
    },
    tile: {
      image: imageUrl,
    },
    header: {
      title: header?.title,
      description: header?.description,
      backgroundColor: header?.backgroundColor,
      color: header?.color,
      mobileImage: header?.mobileImageUrl,
      webImage: header?.imageUrl,
      displayStyle: header?.displayStyle,
    },
    storefrontBanner: {
      active: storefrontBanner?.active || false,
      heading: storefrontBanner?.heading,
      headingColor: storefrontBanner?.headingColor,
      subHeading: storefrontBanner?.subHeading,
      subHeadingColor: storefrontBanner?.subHeadingColor,
      tag: storefrontBanner?.tag,
      tagColor: storefrontBanner?.tagColor,
      cta: storefrontBanner?.cta,
      backgroundColor: storefrontBanner?.backgroundColor,
      bannerImage: storefrontBanner?.image,
    },
    rules: mapApiSubjectsToRules(subjects),
    children,
  }
}
